import * as THREE from 'three'
import type { GSplatSogsResource } from '@/plugins/gsplat/loaders/GSplatSogsResource'
import type { SOGNode } from '@/plugins/gsplat/loaders/SOGLoader'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 八叉树节点 LOD 数据
 */
export interface GSplatOctreeNodeLod {
  /** 文件相对路径 */
  file: string
  /** 文件在 filenames 列表中的索引 */
  fileIndex: number
  /** 该 LOD 在文件中的字节偏移 */
  offset: number
  /** 该 LOD 的 splat 数量 */
  count: number
}

/**
 * 八叉树节点
 */
export class GSplatOctreeNode {
  /** 当前节点的 LOD 数据列表 */
  lods: GSplatOctreeNodeLod[]
  /** 节点本地坐标系下的包围盒 */
  bounds: THREE.Box3

  constructor(
    lods: GSplatOctreeNodeLod[],
    boundData: { min: [number, number, number]; max: [number, number, number] }
  ) {
    this.lods = lods
    this.bounds = new THREE.Box3(
      new THREE.Vector3(boundData.min[0], boundData.min[1], boundData.min[2]),
      new THREE.Vector3(boundData.max[0], boundData.max[1], boundData.max[2])
    )
  }
}

/**
 * 文件元数据
 */
export interface FileMetadata {
  /** 完整资源 URL */
  url: string
  /** LOD 级别，-1 表示未使用 */
  lodLevel: number
}

/**
 * 资源加载器接口
 * 对应 PlayCanvas 的 GSplatAssetLoaderBase
 */
export interface GSplatAssetLoaderInterface {
  /**
   * 触发资源下载，幂等。
   *
   * @param url - 资源完整 URL
   */
  load(url: string): void
  /**
   * 卸载资源并释放引用。
   *
   * @param url - 资源完整 URL
   */
  unload(url: string): void
  /**
   * 获取已加载完成的资源句柄。
   *
   * @param url - 资源完整 URL
   * @returns 资源实例；尚未完成加载返回 null
   */
  getResource(url: string): GSplatSogsResource | null
  /** 查询资源是否已进入不可继续等待的失败终态。 */
  isResourceFailed?(url: string): boolean
  /** 获取当前资源队列与失败计数快照。 */
  getStatus?(): {
    total: number
    loaded: number
    loading: number
    queued: number
    failed: number
  }
  /** 释放加载器与未完成任务。 */
  destroy(): void
  /** 最大并发下载数 */
  maxConcurrentLoads: number
}

/**
 * 临时数组，避免冷却期间的分配
 */
const _toDelete: number[] = []

/**
 * GSplat 八叉树资源管理器
 *
 * 核心功能：
 * 1. 引用计数管理（引用归零进入冷却期，而非立即卸载）
 * 2. 冷却机制（避免频繁加载/卸载同一资源）
 * 3. 资源加载/卸载管理
 * 4. Environment 资源管理
 *
 * 参考 PlayCanvas gsplat-octree.js 的资源管理逻辑
 */
export class GSplatOctree {
  /** 八叉树节点数组 */
  nodes: GSplatOctreeNode[] = []

  /** 文件元数据数组 */
  files: FileMetadata[] = []

  /** LOD 级别数量 */
  lodLevels: number

  /** 容器资源的文件 URL（用作基准路径） */
  assetFileUrl: string

  /** 已加载的文件资源，按文件索引索引 */
  fileResources: Map<number, GSplatSogsResource> = new Map()

  /**
   * Context restore 期间暂时不可用于新 WorldState、但仍可能被旧 WorldState 引用的资源。
   *
   * 资源从 `fileResources` 隐藏后仍需保留这里的引用计数保护，避免 cooldown 误把
   * AssetLoader 当前持有的同一资源销毁。恢复完成或替换资源接管后会清除该记录。
   */
  private restoringFileResources: Map<number, GSplatSogsResource> = new Map()

  /** 文件引用计数，按文件索引索引 */
  fileRefCounts: Int32Array

  /** 尚未完成的文件加载关注计数，按文件索引索引 */
  fileLoadInterests: Int32Array

  /** Files whose references reached zero and are waiting before unload. */
  cooldowns: Map<number, number> = new Map()

  /** Environment 资源 URL（可选） */
  environmentUrl: string | null = null

  /** 已加载的 environment 资源 */
  environmentResource: GSplatSogsResource | null = null

  /** Environment 引用计数 */
  environmentRefCount: number = 0

  /** 资源加载器（外部提供） */
  assetLoader: GSplatAssetLoaderInterface | null = null

  /** 八叉树是否已销毁 */
  destroyed: boolean = false

  /** 是否启用调试日志 */
  private debug: boolean = false

  /** Number of update ticks before unloading zero-ref file resources. */
  private cooldownTicks: number = 100

  /** Last host logical frame that advanced this octree's cooldowns. */
  private lastCooldownTickFrame: number | null = null

  /** Identity of the logical-frame counter used by the last cooldown tick. */
  private lastCooldownTickSource: object | null = null

  constructor(
    assetFileUrl: string,
    data: {
      lodLevels: number
      filenames: string[]
      tree: SOGNode
      environment?: string | undefined
    },
    options?: {
      debug?: boolean
      baseUrl?: string // LOD base URL (decoded when needed)
      // 多域名分片 baseUrl 列表：按 fileIndex 取模分配，用于绕开 HTTP/1.1 单域并发连接限制。
      // 注意：列表顺序必须保持稳定，顺序变化会导致同一文件映射到不同域名，浏览器缓存失效。
      baseUrls?: string[]
      loadEnvironment?: boolean // 是否加载 environment（默认 true）
    }
  ) {
    this.lodLevels = data.lodLevels
    this.assetFileUrl = assetFileUrl
    this.debug = options?.debug ?? false

    // 解析基础目录，用于拼接相对路径
    const baseDir = options?.baseUrl ?? GSplatOctree.resolveBaseUrl(assetFileUrl)

    // 分片目录列表：未提供时退化为单一 baseDir（行为与原先一致）；
    // 仅传域名（如 https://cdn1.example.com）的项自动继承 baseDir 的路径部分
    const shardDirs =
      options?.baseUrls && options.baseUrls.length > 0
        ? options.baseUrls.map((shard) => GSplatOctree.normalizeShardDir(shard, baseDir))
        : [baseDir]

    // 扩展所有文件路径为完整 URL（按 fileIndex 取模分配分片域名，保证同一文件 URL 终生稳定）
    this.files = data.filenames.map((url, index) => ({
      url: this.isRelativePath(url) ? this.joinPath(shardDirs[index % shardDirs.length], url) : url,
      lodLevel: -1,
    }))

    // 初始化引用计数数组
    this.fileRefCounts = new Int32Array(this.files.length)
    this.fileLoadInterests = new Int32Array(this.files.length)

    // 解析可选的 environment 字段（根据 loadEnvironment 选项）
    if (data.environment && options?.loadEnvironment !== false) {
      this.environmentUrl = this.isRelativePath(data.environment)
        ? this.joinPath(baseDir, data.environment)
        : data.environment
    }

    // 提取叶节点
    const leafNodes: Array<{
      lods: Record<string, { file: number; offset: number; count: number }>
      bound: { min: [number, number, number]; max: [number, number, number] }
    }> = []
    this._extractLeafNodes(data.tree, leafNodes)

    // 创建节点
    this.nodes = leafNodes.map((nodeData) => {
      const lods: GSplatOctreeNodeLod[] = []

      // 确保每个 LOD 级别都有条目
      for (let i = 0; i < this.lodLevels; i++) {
        const lodData = nodeData.lods?.[i.toString()]
        if (lodData) {
          lods.push({
            file: this.files[lodData.file].url || '',
            fileIndex: lodData.file,
            offset: lodData.offset || 0,
            count: lodData.count || 0,
          })

          // 记录文件的 LOD 级别
          this.files[lodData.file].lodLevel = i
        } else {
          // 缺失的 LOD 条目，填充默认值
          lods.push({
            file: '',
            fileIndex: -1,
            offset: 0,
            count: 0,
          })
        }
      }

      return new GSplatOctreeNode(lods, nodeData.bound)
    })
  }

  /**
   * 销毁八叉树并清理内部状态
   * 不强制卸载资源，因为它们可能仍被管理器引用
   */
  destroy(): void {
    this.destroyed = true

    // 清理内部状态
    this.fileResources.clear()
    this.restoringFileResources?.clear()
    this.cooldowns.clear()
    this.lastCooldownTickFrame = null
    this.lastCooldownTickSource = null

    // 销毁资源加载器
    if (this.assetLoader) {
      this.assetLoader.destroy()
      this.assetLoader = null
    }

    this.environmentResource = null
  }

  /** Whether zero-reference file resources are still waiting for eviction. */
  get hasPendingCooldowns(): boolean {
    return this.cooldowns.size > 0
  }

  /**
   * 是否仍有能靠下一个逻辑帧推进的 cooldown。
   *
   * 停在一 tick 的资源可能仍受旧 sorted WorldState 或共享加载关注保护。继续渲染并不能
   * 释放这些持有者，因此保留 cooldown 记录等待排序/加载事件唤醒，但不让按需渲染空转。
   */
  get hasCooldownTickWork(): boolean {
    for (const [fileIndex, remaining] of this.cooldowns) {
      if (remaining > 1) {
        return true
      }

      const resource = this.fileResources.get(fileIndex)
      const restoringResource = this.restoringFileResources.get(fileIndex)
      const blockedByActiveUse =
        this.fileLoadInterests[fileIndex] !== 0 ||
        (resource?.refCount ?? 0) !== 0 ||
        (restoringResource?.refCount ?? 0) !== 0

      if (!blockedByActiveUse && this.assetLoader) {
        return true
      }
    }

    return false
  }

  /**
   * 递归提取叶节点（带 lods 属性的节点）
   */
  private _extractLeafNodes(
    node: SOGNode,
    leafNodes: Array<{
      lods: Record<string, { file: number; offset: number; count: number }>
      bound: { min: [number, number, number]; max: [number, number, number] }
    }>
  ): void {
    if (node.lods) {
      // 这是叶节点
      leafNodes.push({
        lods: node.lods,
        bound: node.bound,
      })
    } else if (node.children) {
      // 这是分支节点，递归子节点
      for (const child of node.children) {
        this._extractLeafNodes(child, leafNodes)
      }
    }
  }

  /**
   * 获取文件资源。
   *
   * @param fileIndex - 文件在 filenames 列表中的索引
   * @returns 已加载的资源；尚未加载返回 null
   */
  getFileResource(fileIndex: number): GSplatSogsResource | null {
    const resource = this.fileResources.get(fileIndex)
    if (resource?.gpuReady === false) {
      // 恢复中的资源由 AssetLoader 暂时隐藏；恢复失败进入重载时也必须移除旧缓存，
      // 否则 ensureFileResource 会因 Map 中仍有 key 而永久短路。旧 WorldState 的引用
      // 仍由 restoringFileResources 保护，不能因隐藏缓存而丢失。
      this.restoringFileResources.set(fileIndex, resource)
      this.fileResources.delete(fileIndex)
      return null
    }
    return resource ?? null
  }

  /**
   * 查询文件是否已耗尽加载重试。
   *
   * @param fileIndex - 文件在 filenames 列表中的索引
   */
  isFileFailed(fileIndex: number): boolean {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      return false
    }
    const url = this.files[fileIndex].url
    return this.assetLoader?.isResourceFailed?.(url) === true
  }

  /** 查询 environment 资源是否已耗尽加载重试。 */
  isEnvironmentFailed(): boolean {
    if (!this.environmentUrl) {
      return false
    }
    return this.assetLoader?.isResourceFailed?.(this.environmentUrl) === true
  }

  /**
   * 增加文件引用计数
   *
   * @param fileIndex - 文件索引
   */
  incRefCount(fileIndex: number): void {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      log.error(`[GSplatOctree] 无效的 fileIndex: ${fileIndex}`)
      return
    }

    const count = this.fileRefCounts[fileIndex] + 1
    this.fileRefCounts[fileIndex] = count
    this.cooldowns.delete(fileIndex)
  }

  /**
   * 减少文件引用计数
   * 当引用计数归零时立即卸载资源
   *
   * @param fileIndex - 文件索引
   */
  decRefCount(fileIndex: number, cooldownTicks = this.cooldownTicks): void {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      log.error(`[GSplatOctree] 无效的 fileIndex: ${fileIndex}`)
      return
    }

    const previousCount = this.fileRefCounts[fileIndex]
    if (previousCount <= 0) {
      this.fileRefCounts[fileIndex] = 0
      log.error(`[GSplatOctree] 引用计数异常: fileIndex=${fileIndex}, count=-1`)
      return
    }
    const count = previousCount - 1
    this.fileRefCounts[fileIndex] = count

    // 引用计数归零时进入冷却；即使要求立即卸载，也必须等待尚在使用资源的
    // WorldState 和共享加载关注全部释放。
    if (count === 0) {
      if (cooldownTicks <= 0) {
        if (!this.unloadResourceIfUnused(fileIndex)) {
          this.cooldowns.set(fileIndex, 1)
        }
      } else {
        this.cooldowns.set(fileIndex, cooldownTicks)
      }
    }
  }

  /**
   * 增加文件加载关注计数。
   *
   * 每个 Instance 的 pending/prefetch Set 分别持有一个关注，防止其中一个实例取消
   * 请求时中止另一个实例仍在等待的共享 URL。
   */
  incLoadInterest(fileIndex: number): void {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      log.error(`[GSplatOctree] 无效的 fileIndex: ${fileIndex}`)
      return
    }

    this.fileLoadInterests[fileIndex]++
    this.cooldowns.delete(fileIndex)
  }

  /** 减少文件加载关注计数；完成和取消路径都必须与首次加入 Set 成对调用。 */
  decLoadInterest(fileIndex: number): void {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      log.error(`[GSplatOctree] 无效的 fileIndex: ${fileIndex}`)
      return
    }

    const previousCount = this.fileLoadInterests[fileIndex]
    if (previousCount <= 0) {
      this.fileLoadInterests[fileIndex] = 0
      log.error(`[GSplatOctree] 加载关注计数异常: fileIndex=${fileIndex}, count=-1`)
      return
    }
    this.fileLoadInterests[fileIndex] = previousCount - 1
  }

  /**
   * 取消没有任何引用或加载关注的文件请求。
   *
   * 如果请求恰好已经成功，则接管资源并沿用普通预取的冷却回收路径，避免取消路径
   * 立即销毁刚完成的资源。
   */
  cancelResourceLoadIfUnused(fileIndex: number): void {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      log.error(`[GSplatOctree] 无效的 fileIndex: ${fileIndex}`)
      return
    }

    if (this.fileRefCounts[fileIndex] !== 0 || this.fileLoadInterests[fileIndex] !== 0) {
      return
    }

    const fullUrl = this.files[fileIndex].url
    const loadedResource =
      this.fileResources.get(fileIndex) ?? this.assetLoader?.getResource(fullUrl)
    if (loadedResource) {
      this.fileResources.set(fileIndex, loadedResource)
      this.cooldowns.set(fileIndex, loadedResource.refCount > 0 ? 1 : this.cooldownTicks)
      return
    }

    this.unloadResourceIfUnused(fileIndex)
  }

  /**
   * Advances zero-reference file cooldowns and unloads expired resources.
   *
   * Managers sharing an octree may call this during the same host logical frame. When a frame
   * identity is supplied, only the first call advances the cooldown.
   *
   * @param cooldownTicks - Default cooldown duration for newly released resources.
   * @param frameId - Optional host logical-frame counter used to deduplicate shared-manager updates.
   * @param frameSource - Optional identity of the owner of `frameId`.
   */
  updateCooldownTick(cooldownTicks: number, frameId?: number, frameSource?: object): void {
    this.cooldownTicks = Math.max(0, Math.floor(cooldownTicks))

    if (this.cooldowns.size === 0) {
      return
    }

    const hasFrameIdentity = typeof frameId === 'number' && Number.isFinite(frameId)
    const normalizedFrameId = hasFrameIdentity ? Math.floor(frameId) : null
    const normalizedFrameSource = frameSource ?? null
    if (
      hasFrameIdentity &&
      this.lastCooldownTickFrame === normalizedFrameId &&
      this.lastCooldownTickSource === normalizedFrameSource
    ) {
      return
    }
    if (hasFrameIdentity) {
      this.lastCooldownTickFrame = normalizedFrameId
      this.lastCooldownTickSource = normalizedFrameSource
    }

    this.cooldowns.forEach((remaining, fileIndex) => {
      if (remaining <= 1) {
        if (this.fileRefCounts[fileIndex] !== 0) {
          _toDelete.push(fileIndex)
        } else if (this.unloadResourceIfUnused(fileIndex)) {
          _toDelete.push(fileIndex)
        } else {
          // 旧 sorted WorldState 或共享请求仍在使用该资源。保留一 tick，待其 dec 后回收。
          this.cooldowns.set(fileIndex, 1)
        }
      } else {
        this.cooldowns.set(fileIndex, remaining - 1)
      }
    })

    for (const fileIndex of _toDelete) {
      this.cooldowns.delete(fileIndex)
    }
    _toDelete.length = 0
  }

  /** Immediately unloads every zero-reference resource currently waiting in cooldown. */
  flushCooldowns(): void {
    for (const fileIndex of Array.from(this.cooldowns.keys())) {
      if (this.fileRefCounts[fileIndex] !== 0) {
        this.cooldowns.delete(fileIndex)
      } else if (!this.unloadResourceIfUnused(fileIndex)) {
        // flush 也不能越过旧 WorldState 的资源引用或另一个实例的加载关注。
        this.cooldowns.set(fileIndex, 1)
      }
    }
  }

  /**
   * 卸载文件资源
   *
   * @param fileIndex - 文件索引
   */
  unloadResource(fileIndex: number): void {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      log.error(`[GSplatOctree] 无效的 fileIndex: ${fileIndex}`)
      return
    }

    if (!this.unloadResourceIfUnused(fileIndex)) {
      this.cooldowns.set(fileIndex, 1)
    }
  }

  /** 仅当实例、加载请求和 WorldState 都不再引用文件时才真正卸载。 */
  private unloadResourceIfUnused(fileIndex: number): boolean {
    const resource = this.fileResources.get(fileIndex)
    const restoringResource = this.restoringFileResources.get(fileIndex)
    if (
      this.fileRefCounts[fileIndex] !== 0 ||
      this.fileLoadInterests[fileIndex] !== 0 ||
      (resource?.refCount ?? 0) !== 0 ||
      (restoringResource?.refCount ?? 0) !== 0
    ) {
      return false
    }

    // 如果八叉树已销毁，assetLoader 为 null，无需卸载
    if (!this.assetLoader) {
      return false
    }

    const fullUrl = this.files[fileIndex].url

    // 总是调用 unload - 它处理已加载、加载中和排队的资源
    this.assetLoader.unload(fullUrl)

    // 清理已加载的资源
    if (this.fileResources.has(fileIndex)) {
      this.fileResources.delete(fileIndex)
    }
    this.restoringFileResources.delete(fileIndex)
    this.cooldowns.delete(fileIndex)
    return true
  }

  /**
   * 确保文件资源已加载并可用
   * 此函数会：
   * - 如果未开始加载，则启动加载
   * - 检查加载是否完成，如果可用则存储资源
   *
   * @param fileIndex - 文件索引
   */
  ensureFileResource(fileIndex: number): void {
    if (fileIndex < 0 || fileIndex >= this.files.length) {
      log.error(`[GSplatOctree] 无效的 fileIndex: ${fileIndex}`)
      return
    }

    if (!this.assetLoader) {
      log.error('[GSplatOctree] assetLoader 未设置')
      return
    }

    // 资源已加载；context restore 期间先移除不可用缓存，成功后再从 loader 挂回。
    const cachedResource = this.fileResources.get(fileIndex)
    if (cachedResource) {
      if (cachedResource.gpuReady !== false) {
        return
      }
      this.restoringFileResources.set(fileIndex, cachedResource)
      this.fileResources.delete(fileIndex)
    }

    // 检查资源是否从加载器可用
    const fullUrl = this.files[fileIndex].url
    const res = this.assetLoader.getResource(fullUrl)

    if (res) {
      this.fileResources.set(fileIndex, res)
      // 恢复成功时 res 通常就是原对象；恢复失败重载时则是替换对象。两种情况都已由
      // AssetLoader 接管新资源，旧失败资源会通过 retireWhenUnused 自行等 WorldState 释放。
      this.restoringFileResources.delete(fileIndex)
      if (this.fileRefCounts[fileIndex] === 0) {
        this.cooldowns.set(fileIndex, this.cooldownTicks)
      }

      return
    }

    // 启动/继续加载（加载器内部处理重复请求）
    this.assetLoader.load(fullUrl)
  }

  /**
   * 增加 environment 引用计数
   */
  incEnvironmentRefCount(): void {
    this.environmentRefCount++
  }

  /**
   * 减少 environment 引用计数
   * 引用计数归零时立即卸载
   */
  decEnvironmentRefCount(): void {
    if (this.environmentRefCount <= 0) {
      this.environmentRefCount = 0
      log.error('[GSplatOctree] environmentRefCount 异常: -1')
      return
    }
    this.environmentRefCount--

    // 引用计数归零时立即卸载
    if (this.environmentRefCount === 0) {
      this.unloadEnvironmentResource()
    }
  }

  /**
   * 确保 environment 资源已加载并可用
   */
  ensureEnvironmentResource(): void {
    // 如果八叉树已销毁，不加载任何内容
    if (!this.assetLoader) {
      return
    }

    // 未配置 environment
    if (!this.environmentUrl) {
      return
    }

    // 资源已加载
    if (this.environmentResource) {
      return
    }

    // 检查资源是否从加载器可用
    const res = this.assetLoader.getResource(this.environmentUrl)
    if (res) {
      this.environmentResource = res

      // 如果加载完成但不需要，立即卸载
      if (this.environmentRefCount === 0) {
        this.unloadEnvironmentResource()
      }

      return
    }

    // 启动/继续加载
    this.assetLoader.load(this.environmentUrl)
  }

  /**
   * 卸载 environment 资源
   */
  unloadEnvironmentResource(): void {
    try {
      if (this.assetLoader && this.environmentUrl) {
        // 总是调用 unload，让加载器同时处理已加载、加载中和排队中的资源。
        this.assetLoader.unload(this.environmentUrl)
      }
    } finally {
      this.environmentResource = null
    }
  }

  // ==================== 工具方法 ====================

  /**
   * 获取目录路径（去掉文件名），支持 /local-file/ 代理编码与 ?path= 查询参数格式。
   *
   * @param filePath - 源文件路径或 URL
   * @param options - encode=true 时对结果目录部分做 encodeURIComponent
   * @returns 以目录结尾的 URL 前缀，可直接拼接相对文件名
   */
  static resolveBaseUrl(filePath: string, options?: { encode?: boolean }): string {
    // 检查是否是 ?path= 查询参数格式 (例如: http://example.com?path=C:\folder\file.sog)
    const queryPathMatch = filePath.match(/^([^?]+\?path=)(.*)$/)
    if (queryPathMatch) {
      const prefix = queryPathMatch[1] // http://www.sharefe.com?path=
      const actualPath = queryPathMatch[2] // E:\\SHARE_WORKSPACE\\...\\file.sog
      let decodedActualPath = actualPath
      try {
        decodedActualPath = decodeURIComponent(actualPath)
      } catch {
        decodedActualPath = actualPath
      }

      // 找到目录部分（去掉文件名）
      const lastSlash = Math.max(
        decodedActualPath.lastIndexOf('/'),
        decodedActualPath.lastIndexOf('\\')
      )
      if (lastSlash >= 0) {
        const directory = decodedActualPath.substring(0, lastSlash + 1)
        const shouldEncode = options?.encode ?? actualPath.includes('%')
        return prefix + (shouldEncode ? encodeURIComponent(directory) : directory)
      }
      return prefix
    }

    // 检查是否是 /local-file/ 代理路径（整个本地路径被编码为单个 URL 段）
    const localFileMatch = filePath.match(/^(https?:\/\/[^/]+\/local-file\/)(.+)$/)
    if (localFileMatch) {
      const prefix = localFileMatch[1] // http://localhost:8003/local-file/
      const encodedLocalPath = localFileMatch[2] // C%3A%2Fcode%2F...%2Flod-meta.json

      // 解码本地路径
      let decodedLocalPath: string
      try {
        decodedLocalPath = decodeURIComponent(encodedLocalPath)
      } catch {
        decodedLocalPath = encodedLocalPath
      }

      // 找到目录部分（去掉文件名）
      const lastSlash = Math.max(
        decodedLocalPath.lastIndexOf('/'),
        decodedLocalPath.lastIndexOf('\\')
      )
      if (lastSlash >= 0) {
        const localDir = decodedLocalPath.substring(0, lastSlash + 1)
        // 重新编码整个本地目录路径
        return prefix + encodeURIComponent(localDir)
      }
      return prefix
    }

    // 标准路径处理
    const lastSlash = Math.max(filePath.lastIndexOf('/'), filePath.lastIndexOf('\\'))
    return lastSlash >= 0 ? filePath.substring(0, lastSlash + 1) : ''
  }

  /**
   * 规范化分片目录：仅传域名（无路径、无查询参数）时，用该域名替换 baseDir 的 origin，
   * 继承 baseDir 的路径/查询部分（兼容 ?path= 代理格式）；带路径的项按原样使用。
   *
   * @param shard - 分片域名或完整目录前缀
   * @param baseDir - 主 baseUrl
   * @returns 规范化后的目录前缀
   */
  static normalizeShardDir(shard: string, baseDir: string): string {
    let shardUrl: URL
    try {
      shardUrl = new URL(shard)
    } catch {
      // shard 不是绝对 URL，无法推导，按原样使用
      return shard
    }

    if (shardUrl.pathname !== '/' || shardUrl.search || shardUrl.hash) {
      return shard
    }

    try {
      const baseUrl = new URL(baseDir)
      return shardUrl.origin + baseDir.slice(baseUrl.origin.length)
    } catch {
      const normalizedBaseDir = baseDir.startsWith('/') ? baseDir : `/${baseDir}`
      return shardUrl.origin + normalizedBaseDir
    }
  }

  /**
   * 判断路径是否为相对路径
   */
  private isRelativePath(path: string): boolean {
    // 简单判断：不包含协议且不以 / 开头
    return !path.includes('://') && !path.startsWith('/')
  }

  /**
   * 拼接路径
   * 支持 /local-file/ 代理的编码路径和 ?path= 查询参数格式
   */
  private joinPath(base: string, relative: string): string {
    // 检查是否是 ?path= 查询参数格式
    const queryPathMatch = base.match(/^([^?]+\?path=)(.*)$/)
    if (queryPathMatch) {
      const prefix = queryPathMatch[1] // http://www.sharefe.com?path=
      const basePath = queryPathMatch[2] // E:\\SHARE_WORKSPACE\\...\\directory\
      let decodedBasePath = basePath
      try {
        decodedBasePath = decodeURIComponent(basePath)
      } catch {
        decodedBasePath = basePath
      }

      // 规范化路径分隔符（统一使用反斜杠，匹配 Windows 路径风格）
      const separator = decodedBasePath.includes('\\') ? '\\' : '/'

      // 确保基础路径以分隔符结尾
      let normalizedBase = decodedBasePath
      if (!normalizedBase.endsWith('/') && !normalizedBase.endsWith('\\')) {
        normalizedBase += separator
      }

      // 拼接相对路径（替换相对路径中的分隔符为一致的格式）
      const normalizedRelative = relative.replace(/[/\\]/g, separator)
      const fullPath = normalizedBase + normalizedRelative
      const shouldEncode = basePath.includes('%')
      return prefix + (shouldEncode ? encodeURIComponent(fullPath) : fullPath)
    }

    // 检查是否是 /local-file/ 代理路径
    const localFileMatch = base.match(/^(https?:\/\/[^/]+\/local-file\/)(.+)$/)
    if (localFileMatch) {
      const prefix = localFileMatch[1] // http://localhost:8003/local-file/
      const encodedLocalDir = localFileMatch[2] // C%3A%2Fcode%2F...%2Fshow%2F

      // 解码本地目录路径
      let decodedLocalDir: string
      try {
        decodedLocalDir = decodeURIComponent(encodedLocalDir)
      } catch {
        decodedLocalDir = encodedLocalDir
      }

      // 确保目录路径以 / 或 \ 结尾
      if (!decodedLocalDir.endsWith('/') && !decodedLocalDir.endsWith('\\')) {
        decodedLocalDir += '/'
      }

      // 拼接相对路径并重新编码
      const fullLocalPath = decodedLocalDir + relative
      return prefix + encodeURIComponent(fullLocalPath)
    }

    // 标准路径拼接
    // 移除 base 末尾的 /
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base
    // 移除 relative 开头的 /
    const cleanRelative = relative.startsWith('/') ? relative.slice(1) : relative
    return `${cleanBase}/${cleanRelative}`
  }
}
